import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import type { MeetingParticipant, SpeakerStats } from './useConversationDetail';

export interface TeamConversationDetailData {
  id: string; conversation_title?: string | null; topic: string; summary: string;
  site: string; compacted_text: string; estimated_tokens: number; turn_count: number;
  entity_tags: string[]; first_captured_at: string; last_captured_at: string;
  author_name: string; author_role: string; project_name?: string | null;
  team_project_id?: string | null; team_project_name?: string | null;
  // Granola-only; nullish elsewhere (only whole-team, non-private meetings reach the team surface).
  meeting_participants?: MeetingParticipant[] | null;
  speaker_stats?: SpeakerStats | null;
  // Conversation note redesign (PR1 route additions). note_doc stays `unknown` at the hook layer —
  // deriveNoteDoc (src/lib/note-doc.ts) is the narrow waist that validates/derives before render.
  note_doc?: unknown;
  briefing_category?: string | null;
  entry_kind?: string | null;
  // PR-3 "Open in source" — same field/semantics as ConversationDetailData. Declared on BOTH
  // interfaces deliberately: the shared surface takes their union, so an unguarded data.source_url
  // only typechecks when neither arm is missing it.
  source_url?: string | null;
}

export function useTeamConversationDetail(id: string) {
  const query = useQuery({
    queryKey: ['surfaces', 'team-conversations', id],
    queryFn: () => apiFetch<TeamConversationDetailData>(`/api/surfaces/team/conversations/${id}`),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id,
  });
  return { ...query, data: query.data ?? undefined };
}
